import { useRef, useState } from "react";
import { IReportMetricsRow } from "../../../../@types/reports-metrics.type";
import { SelectedType } from "./../../index";
import Box from "@mui/material/Box";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import { AgGridReact, CustomCellRendererProps } from "ag-grid-react";
import { themeGrid } from "../../../../themeGrid";
import { localeText } from "../../../../localeTextFromGrid";
import { ColDef } from "ag-grid-community";
import { GridToPrint } from "../GridToPrint";
import { handlePrintFilteredRows } from "../../utils/handlePrintFilteredRows";
import styles from "./styles.module.scss";
import iconExcel from "./../../../../assets/icon-excel.svg";
import { downloadResourcesReportToExcel } from "../../../../services/api/resource/resourceService";
import { Button } from "../../../../components/Button";
import { calculateTimeRange } from "../../../../utils/timeUtils";

export interface RowDataResourceGrid {
  id?: string;
  name: string;
  totalScheduled: string;
  percentScheduled?: number;
  billed: string;
  notBilled: string;
  notAllocated?: string;
  projection?: string;
  unavailability: string;
}

interface DefaultDataProps {
  rows: RowDataResourceGrid[];
  rowCount: number;
  selectedType: SelectedType | null;
  setRowSelected: React.Dispatch<
    React.SetStateAction<IReportMetricsRow | null>
  >;
  setValueTab: React.Dispatch<React.SetStateAction<number>>;
  setSelectedType: React.Dispatch<React.SetStateAction<SelectedType | null>>;
  filterState: any;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const parseValue = (value: string): number =>
  parseFloat(value.replace(/[hd]/, "")) || 0;

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3, padding: 0 }}>{children}</Box>}
    </div>
  );
}

export const ResourcesDataGridReport: React.FC<DefaultDataProps> = ({
  rows = [],
  rowCount = 0,
  selectedType,
  setRowSelected,
  setValueTab,
  setSelectedType,
  filterState
}) => {
  const [value, setValue] = useState(0);
  const [filteredRowsForPrint, setFilteredRowsForPrint] = useState<
    RowDataResourceGrid[]
  >([]);
  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };
  const gridRef = useRef<AgGridReact<any>>(null);

  const columns: ColDef[] = [
    {
      field: "name",
      headerName: "Colaborador",
      flex: 2,
      minWidth: 100,
    },
    {
      field: "totalScheduled",
      headerName: "Total Agendadas",
      flex: 1,
      minWidth: 70,
      comparator: (v1: string, v2: string) => parseValue(v1) - parseValue(v2),
    },
    {
      field: "billed",
      headerName: "Faturável",
      minWidth: 70,
      flex: 1,
      comparator: (v1: string, v2: string) => parseValue(v1) - parseValue(v2),
    },
    {
      field: "notBilled",
      headerName: "Não Faturável",
      flex: 1,
      minWidth: 70,
      comparator: (v1: string, v2: string) => parseValue(v1) - parseValue(v2),
    },
    {
      field: "unavailability",
      headerName: "Indisponivel",
      flex: 1,
      minWidth: 70,
      comparator: (v1: string, v2: string) => parseValue(v1) - parseValue(v2),
    },
  ];

  const additionalColumns: ColDef[] = [
    {
      field: "notAllocated",
      headerName: "Não Alocadas",
      flex: 1,
      minWidth: 70,
      comparator: (v1: string, v2: string) => parseValue(v1) - parseValue(v2),
    },
    {
      field: "projection",
      headerName: "Projetadas",
      flex: 1,
      minWidth: 70,
      comparator: (v1: string, v2: string) => parseValue(v1) - parseValue(v2),
    },
    {
      field: "percentScheduled",
      headerName: "Utilização",
      flex: 2,
      minWidth: 100,
      filter: false,
      cellRenderer: (params: CustomCellRendererProps) => {
        const percent = params.data.percentScheduled;
        const displayPercent = Math.min(percent, 100);
        const progressBarColor = percent > 100 ? "#F45B5B" : "";
        return (
          <div className={styles.container}>
            <div className={styles.progressBarContainer}>
              <div
                className={styles.progressBar}
                style={{
                  width: `${displayPercent}%`,
                  backgroundColor: progressBarColor,
                }}
                title={`${percent}%`}
              />
            </div>
            <span className={styles.totalText}>{`${percent}%`}</span>
          </div>
        );
      },
    },
  ];

  const updatedColumns =
    selectedType === "resource" ? [...columns, ...additionalColumns] : columns;

  function handleFilteredForPrint() {
    handlePrintFilteredRows({ gridRef, setFilteredRowsForPrint, rows });
  }

  async function ExportToExcel() {
    const { startDate, endDate } = calculateTimeRange(filterState?.currentPeriod, filterState?.period);
    const filter = { startTime: startDate.startOf('day').toISOString(), endTime: endDate.startOf('day').toISOString(), timeType: 1 } as IReportMetricsBody;

    downloadResourcesReportToExcel(filter);
  }

  return (
    <>
      <main className={styles.dataGridStyle}>
        <Box className={styles.customBox}>
          <Box className={styles.borderedBox}>
            <Tabs
              value={value}
              onChange={handleChange}
              aria-label="basic tabs example"
              classes={{
                flexContainer: styles.customFlexContainer,
                indicator: styles["MuiTabs-indicator"],
              }}
            >
              <Tab label="Tabela de Alocação" className={styles.tab} />
              <Box sx={{ marginLeft: "auto", display: "flex" }}>
                <Button
                  onClick={() => {
                    ExportToExcel();
                  }}
                  iconUrl={iconExcel}
                  className={styles.btnExcel}
                />
              </Box>
            </Tabs>
          </Box>
        </Box>
        <CustomTabPanel value={value} index={0}>
          <div className={`${styles.grid} no-print`}>
            <AgGridReact
              ref={gridRef}
              rowData={rows}
              columnDefs={updatedColumns}
              theme={themeGrid}
              localeText={localeText}
              overlayNoRowsTemplate="<span style='color: gray;'>Sem registros encontrados</span>"
              defaultColDef={{
                sortable: true,
                editable: false,
                filter: true,
                floatingFilter: true,
              }}
              suppressCellFocus={true}
              rowStyle={{ cursor: "pointer" }}
              onRowClicked={(row) => {
                setRowSelected(row.data);
                setValueTab(1);
                setSelectedType("resource");
              }}
              animateRows={true}
              onGridReady={handleFilteredForPrint}
              onFilterChanged={handleFilteredForPrint}
            />
          </div>

          <GridToPrint
            columns={updatedColumns}
            rows={filteredRowsForPrint.length > 0 ? filteredRowsForPrint : rows}
            typeGrid="resources"
          />
        </CustomTabPanel>
      </main>
    </>
  );
};
